import { Injectable, inject } from '@angular/core';

import { deepEqual } from 'fast-equals';
import type { Observable } from 'rxjs';
import { BehaviorSubject, of } from 'rxjs';
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  publishReplay,
  refCount,
  switchMap,
} from 'rxjs/operators';

import { filterBoolean } from '@core/common-legacy';

import { BulkActionsController } from './bulk-actions.controller';
import type { BulkItemBase, BulkSelectionTrackerItemFilter } from './bulk-actions.types';
import { BULK_SELECTION_TRACKER_ITEM_FILTER } from './bulk-actions.types';

@Injectable()
export class BulkActionsSelectionTracker {
  private readonly bulkCtrl = inject(BulkActionsController);
  private readonly itemFilter = inject<BulkSelectionTrackerItemFilter>(
    BULK_SELECTION_TRACKER_ITEM_FILTER,
    { optional: true },
  );

  public state$: Observable<TrackerState>;
  public _items$: BehaviorSubject<BulkItemBase[]> = new BehaviorSubject<BulkItemBase[]>([]);
  public items$ = this._items$.pipe(
    switchMap(items => (this.itemFilter ? this.itemFilter(items) : of(items))),
  );

  public set items(items: BulkItemBase[]) {
    this._items$.next(items);
  }

  constructor() {
    this.state$ = this.bulkCtrl.selectedItems$.asObservable().pipe(
      filterBoolean,
      combineLatest(this.items$.pipe(filter(items => !!items)), (selectedItems, allItems) => {
        const totalSelectedCount = selectedItems.length;

        const allItemIds = new Set(allItems.map(i => i.id));
        let currentViewSelectedCount = 0;
        for (const item of selectedItems) {
          if (allItemIds.has(item.id)) currentViewSelectedCount++;
        }

        const shouldDisplayMessage = totalSelectedCount > currentViewSelectedCount;
        return {
          totalSelectedCount,
          currentViewSelectedCount,
          currentViewTotalCount: allItems.length,
          shouldDisplayMessage,
        };
      }),
      distinctUntilChanged(deepEqual),
      publishReplay(),
      refCount(),
    );
  }
}

type TrackerState = {
  totalSelectedCount: number;
  currentViewSelectedCount: number;
  currentViewTotalCount: number;
  shouldDisplayMessage: boolean;
};
